import { useState } from 'react';
import { PILLARS, type Pillar, type ReportBehaviorDto, type ReportSeriesDto, type ReportSeriesKey } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';

/**
 * Label, track, readout — shared by every row and by the scale legend beneath
 * them, so the ruler lines up with the dots it describes.
 */
const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_120px] gap-x-3.5 sm:grid-cols-[minmax(0,1fr)_300px_108px]';

type Props = {
  behaviors: ReportBehaviorDto[];
  series: ReportSeriesDto[];
};

/**
 * The twelve behaviors, two groups at a time.
 *
 * A connected dot plot rather than bars: the question is not "how big is each
 * value" but "how far apart are these two views of the same behavior", and the
 * gap between two dots answers that directly. Bars would put twenty-four
 * lengths on screen and leave the reader to subtract them.
 *
 * The scale runs 1 to 5 across the whole track, always — never zoomed to the
 * data. A leader comparing this cycle to the next one must be looking at the
 * same ruler both times.
 */
/**
 * One mark on the track, with its value.
 *
 * The value used to ride on the `title` attribute, which is the browser's own
 * tooltip: it waits about a second before appearing, and a second is long
 * enough that most people never find out the number is there. Reading a value
 * off a dot is the second thing anyone does on this chart, so it opens on
 * hover with no delay at all.
 *
 * The hit area is a transparent square around the dot rather than the dot
 * itself — a 9px circle is a hard target with a mouse and an impossible one
 * with a thumb.
 */
const Dot = ({
  left,
  label,
  className,
}: {
  left: number;
  label: string;
  className: string;
}) => (
  <span
    className="group absolute top-1/2 z-10 grid size-8 -translate-x-1/2 -translate-y-1/2 place-items-center"
    style={{ left: `${left}%` }}
  >
    <span aria-hidden className={`rounded-full ${className}`} />

    {/* Screen readers get the value inline; they have no use for a hover. */}
    <span className="sr-only">{label}</span>

    <span
      aria-hidden
      className="pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded-lg bg-ink px-2 py-1 font-mono text-[11px] whitespace-nowrap text-white opacity-0 group-hover:opacity-100"
    >
      {label}
    </span>
  </span>
);

export const BehaviorDotPlot = ({ behaviors, series }: Props) => {
  const { t } = useI18n();

  // Open on the comparison the report is about: your view against everyone
  // else's. If either is unavailable, fall back to whatever can be shown.
  const firstAvailable = series.find((entry) => entry.available)?.key ?? 'GLOBAL';
  const [seriesA, setSeriesA] = useState<ReportSeriesKey>(
    series.find((entry) => entry.key === 'SELF')?.available ? 'SELF' : firstAvailable,
  );
  const [seriesB, setSeriesB] = useState<ReportSeriesKey | 'NONE'>(
    series.find((entry) => entry.key === 'GLOBAL')?.available ? 'GLOBAL' : 'NONE',
  );

  /** 1 → 0%, 5 → 100%. */
  const position = (value: number) => ((value - 1) / 4) * 100;

  const label = (key: ReportSeriesKey) => t(`report.series.${key}`);

  const picker = (
    value: string,
    onChange: (next: string) => void,
    includeNone: boolean,
  ) => (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="cursor-pointer rounded-lg border border-line bg-paper px-2.5 py-1.5 text-[13px] outline-none focus:border-teal"
    >
      {includeNone && <option value="NONE">{t('report.seriesNone')}</option>}
      {series.map((entry) => (
        <option key={entry.key} value={entry.key} disabled={!entry.available}>
          {label(entry.key)}
          {/* A group that exists but cannot be shown says why, with its count,
              instead of quietly vanishing from the list. The reason matters:
              five stakeholders folded to protect a single peer is not
              something anybody guesses from "not available". */}
          {entry.available
            ? ''
            : ` (${entry.raters} · ${t(`report.seriesReason.${entry.reason ?? 'NOT_DISCLOSED'}`)})`}
        </option>
      ))}
    </select>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-line bg-sand px-3.5 py-2.5">
        <span className="flex items-center gap-2">
          <span aria-hidden className="size-[9px] rounded-full bg-ink ring-2 ring-paper" />
          <span className="font-mono text-[9.5px] tracking-[0.1em] text-faint uppercase">
            {t('report.seriesA')}
          </span>
          {picker(seriesA, (next) => setSeriesA(next as ReportSeriesKey), false)}
        </span>

        <span className="text-[12px] text-faint">{t('report.versus')}</span>

        <span className="flex items-center gap-2">
          <span aria-hidden className="size-[15px] rounded-full bg-teal ring-3 ring-teal/15" />
          <span className="font-mono text-[9.5px] tracking-[0.1em] text-faint uppercase">
            {t('report.seriesB')}
          </span>
          {picker(seriesB, (next) => setSeriesB(next as ReportSeriesKey | 'NONE'), true)}
        </span>
      </div>

      {PILLARS.map((pillar: Pillar) => {
        const rows = behaviors.filter((behavior) => behavior.pillar === pillar);
        if (rows.length === 0) return null;

        return (
          <div key={pillar} className="mt-5">
            <div className="mx-0.5 mb-0.5 flex items-baseline gap-2.5">
              <span className="font-mono text-[12px] font-bold tracking-[0.14em] text-teal-deep uppercase">
                {t(`pillars.${pillar}`)}
              </span>
              <span className="text-[12px] text-faint">
                · {t(`pillars.${pillar}_motto`)}
              </span>
            </div>

            {rows.map((behavior) => {
              const a = behavior.bySeries[seriesA];
              const b = seriesB === 'NONE' ? undefined : behavior.bySeries[seriesB];
              const delta = a !== undefined && b !== undefined ? a - b : null;

              return (
                <div
                  key={behavior.behaviorId}
                  className={`${ROW_GRID} items-center border-t border-line-soft py-2.5`}
                >
                  <span className="text-[14px] font-semibold">
                    <span className="mr-1.5 font-mono text-[12px] font-bold text-teal">
                      {String(behavior.number).padStart(2, '0')}
                    </span>
                    {behavior.name}
                  </span>

                  <span className="relative col-span-2 h-[26px] sm:col-span-1">
                    <span
                      aria-hidden
                      className="absolute top-1/2 right-1.5 left-1.5 h-0.5 -translate-y-1/2 rounded bg-line"
                    />
                    {a !== undefined && b !== undefined && (
                      <span
                        aria-hidden
                        className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded bg-teal/25"
                        style={{
                          left: `${Math.min(position(a), position(b))}%`,
                          width: `${Math.abs(position(a) - position(b))}%`,
                        }}
                      />
                    )}
                    {b !== undefined && (
                      <Dot
                        left={position(b)}
                        label={`${label(seriesB as ReportSeriesKey)} · ${b.toFixed(2)}`}
                        className="size-[15px] bg-teal ring-3 ring-teal/15"
                      />
                    )}
                    {/* A is drawn smaller and last, so when the two groups
                        agree exactly it sits inside B as a core rather than
                        hiding it — "they both say 4.0" stays visible as two
                        marks, not one. The size difference also separates the
                        series without relying on colour. */}
                    {a !== undefined && (
                      <Dot
                        left={position(a)}
                        label={`${label(seriesA)} · ${a.toFixed(2)}`}
                        className="size-[9px] bg-ink ring-2 ring-paper"
                      />
                    )}
                  </span>

                  <span className="hidden text-right font-mono text-[10.5px] whitespace-nowrap text-faint sm:block">
                    {a !== undefined && `${a.toFixed(1)}`}
                    {delta !== null && (
                      <>
                        {' / '}
                        {b?.toFixed(1)}
                        <span className="ml-1.5 text-soft">
                          {delta >= 0 ? '+' : ''}
                          {delta.toFixed(1)}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* The ruler belongs under the track and nowhere else. Spread across the
          full row it would label positions the dots never occupy. */}
      <div className={`${ROW_GRID} mt-3`}>
        <span className="hidden sm:block" />
        <span className="col-span-2 flex justify-between px-1 font-mono text-[9.5px] tracking-[0.06em] text-faint uppercase sm:col-span-1">
          <span>1 · {t('scale.step1')}</span>
          <span>3 · {t('scale.step3')}</span>
          <span>5 · {t('scale.step5')}</span>
        </span>
        <span className="hidden sm:block" />
      </div>
    </div>
  );
};
